import type { BusPoi } from '@utils/../types/bus.ts';

import type {
  Coordinate,
  MapControllerConfig,
  MapFeatureThemer,
  MapInitialized,
  MapRendererApp,
  VenueStructure,
} from './types.ts';
import { setSource, createMapFeature } from './utils.ts';

type BuildingLabelFeature = {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: Coordinate };
  properties: {
    dynamicLabel: 'building';
    text: string;
    buildingId: string | number;
    category: 'dynamicLabel';
    terminalPoiLabel?: true;
  };
};

type StructureWithBounds = VenueStructure & {
  bounds: NonNullable<VenueStructure['bounds']>;
};

type TerminalPoiPosition = {
  latitude?: number;
  longitude?: number;
  buildingId?: string | number;
};

export default function LabelController(
  app: MapRendererApp,
  mapInitialized: MapInitialized,
  getTerminalPois: () => Promise<BusPoi[]>,
  config: MapControllerConfig,
) {
  const featureThemer = app.bus.get<undefined, MapFeatureThemer>('map/getMapFeatureThemer');

  const initMapSources = (sources: Record<string, unknown>) => {
    return { ...sources, llBuildingLabelsSource: createMapFeature([]) };
  };

  app.bus.on<{ structures: VenueStructure[] }>('venueData/mapDataLoaded', async ({ structures }) => {
    const themer = await featureThemer;
    const buildingLabelFeatures = config.terminalPoiLabels
      ? getTerminalPoiLabelFeatures(await getTerminalPois(), themer)
      : getBuildingLabelFeatures(structures, themer);
    mapInitialized().then(map => setSource('llBuildingLabelsSource', buildingLabelFeatures, map));
  });

  // accept when shouldDisplay is null or undefined or true
  const shouldDisplayPredicate = (building: VenueStructure) => building.shouldDisplay == null || building.shouldDisplay;

  const hasBounds = (structure: VenueStructure): structure is StructureWithBounds => Boolean(structure.bounds);

  // used for both (old) dynamic building labels AND labels for buildings from POIs with category: terminal
  const buildLabelFeature = (
    coordinates: Coordinate,
    text: string,
    buildingId: string | number,
  ): BuildingLabelFeature => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates },
    properties: {
      dynamicLabel: 'building',
      text,
      buildingId,
      category: 'dynamicLabel',
    },
  });

  const getBuildingLabelFeatures = (structures: VenueStructure[], themer: MapFeatureThemer): BuildingLabelFeature[] =>
    structures
      .filter(shouldDisplayPredicate)
      .filter(hasBounds)
      .map(structure => {
        const { bounds } = structure;
        const buildingCenterCoords: Coordinate = [(bounds.e + bounds.w) * 0.5, (bounds.n + bounds.s) * 0.5];
        return themer(buildLabelFeature(buildingCenterCoords, structure.name ?? '', structure.id));
      });

  const hasCoordinates = (
    poi: BusPoi,
  ): poi is BusPoi & { position: TerminalPoiPosition & { latitude: number; longitude: number } } => {
    const position = poi.position as TerminalPoiPosition | undefined;
    return typeof position?.latitude === 'number' && typeof position?.longitude === 'number';
  };

  const getTerminalPoiLabelFeatures = (pois: BusPoi[], themer: MapFeatureThemer): BuildingLabelFeature[] =>
    pois.filter(hasCoordinates).map(poi => {
      const { latitude, longitude, buildingId } = poi.position;
      const coordinates: Coordinate = [longitude, latitude];
      const labelFeature = buildLabelFeature(coordinates, poi.name ?? '', buildingId ?? poi.poiId ?? poi.id ?? '');
      // Tag terminal-POI labels so the click handler animation to POI lat lng
      labelFeature.properties.terminalPoiLabel = true;
      return themer(labelFeature);
    });

  return { initMapSources };
}
